/**
 * Dealer-friendly labels for the actual-publish observability surface
 * (Phase 6.y).
 *
 * Why this file exists
 * --------------------
 * The Preview helpers in `@/utils/publishPreviewStageLabels.ts` describe
 * what *will* / *may* happen if the user publishes — predictive wording
 * is appropriate there because previews are simulations.
 *
 * On the actual Publish Flow surface we are reporting what *did* happen
 * during a completed publish run. Predictive wording would actively
 * mislead support and dealers ("Will change" suggests pending action).
 *
 * The strict separation also keeps the two helper files independently
 * evolvable: changes to past-tense publish copy can never bleed into
 * the Preview UI by accident.
 *
 * Anti-jargon rule
 * ----------------
 * Templates and components MUST NOT render the raw backend identifiers
 * (stage ids, availability tokens, decision-status tokens). Use these
 * helpers exclusively. The companion test
 * `tests/unit/utils/publishFlowStageLabels.test.ts` enforces that no
 * forward-looking verbs (`will`, `may`, `would`, `could change`) leak
 * into any returned string.
 */
import type {
  PublishDecisionRecord,
  PublishDecisionStatus,
  PublishStageAvailability,
} from '@/services/publishPreviewArtifactsService'
import type { PublishObservedStage } from '@/services/publishFlowObservabilityService'

/**
 * Top-level Publish Flow page cards (dealer-facing pipeline).
 * Feed Field Rules are nested inside Field Mapping — not a separate card.
 */
export const PUBLISH_FLOW_PAGE_STAGE_ORDER = [
  'preProcessor',
  'fieldMapping',
  'groups',
  'listingRules',
] as const

/**
 * Full stage ordering for breakdown tables, dialog tabs, and VIN-trail
 * sorting. Includes nested/technical stages such as `feedFieldRules`.
 */
export const PUBLISH_FLOW_STAGE_ORDER = [
  'preProcessor',
  'feedFieldRules',
  'fieldMapping',
  'groups',
  'listingRules',
] as const

export function stageSortIndex(stage: string): number {
  const index = PUBLISH_FLOW_STAGE_ORDER.indexOf(stage as typeof PUBLISH_FLOW_STAGE_ORDER[number])
  return index === -1 ? Number.MAX_SAFE_INTEGER : index
}

export function friendlyPublishFlowStageLabel(stage: PublishObservedStage | string): string {
  switch (stage) {
    case 'preProcessor':
      return 'Feed Preparation'
    case 'inventorySync':
      return 'Inventory Sync'
    case 'feedFieldRules':
      return 'Feed Field Rules'
    case 'fieldMapping':
      return 'Field Mapping'
    case 'groups':
      return 'Vehicle Groups'
    case 'listingRules':
      return 'Website Visibility'
    case 'feedIntake':
      return 'Parsed Feed Vehicles'
    default:
      return 'Publish Stage'
  }
}

/** Dealer-friendly status chip labels for Publish Flow stage cards. */
export type PublishFlowStageDetailStatus =
  | 'available'
  | 'empty'
  | 'notConfigured'
  | 'skipped'
  | 'unsupported'
  | 'error'

export type PublishFlowStageChipInput = {
  status: PublishFlowStageDetailStatus
  changedCount?: number | null
  /** Decision artifact pointers exist for this stage. */
  hasDecisionRecords?: boolean
}

/**
 * Outcome-oriented chip label for Publish Flow stage cards.
 * Maps observability detail status + change counts to dealer-facing copy.
 */
export function publishFlowStageChipLabel(input: PublishFlowStageChipInput): string {
  const { status, changedCount, hasDecisionRecords } = input

  switch (status) {
    case 'skipped':
      return 'Not needed'
    case 'notConfigured':
      return 'Not configured'
    case 'error':
      return 'Needs attention'
    case 'unsupported':
      return 'Not captured yet'
    case 'empty':
      return 'No changes'
    case 'available': {
      const changed =
        typeof changedCount === 'number' && Number.isFinite(changedCount)
          ? Math.max(0, Math.floor(changedCount))
          : null
      if (changed !== null) {
        return changed > 0 ? 'Checked' : 'No changes'
      }
      if (hasDecisionRecords) return 'No changes'
      return 'Checked'
    }
    default:
      return 'Not captured yet'
  }
}

export function publishFlowStageChipColor(
  label: string,
  detailStatus?: PublishFlowStageDetailStatus,
): string {
  if (label === 'Needs attention') return 'error'
  if (label === 'Checked' || label === 'All passed') return 'success'
  if (label === 'No changes') return 'info'
  if (detailStatus === 'available') return 'success'
  if (detailStatus === 'empty') return 'info'
  return 'grey'
}

/** @deprecated Prefer publishFlowStageChipLabel — maps legacy internal chip tokens. */
export function friendlyPublishFlowStatusChipLabel(label: string): string {
  switch (label) {
    case 'Captured':
    case 'Measured':
      return 'Checked'
    case 'No drops':
      return 'All passed'
    case 'Skipped':
      return 'Not needed'
    case 'Empty':
    case 'No vehicles affected':
      return 'No changes'
    case 'Not Configured':
      return 'Not configured'
    case 'Error':
      return 'Needs attention'
    case 'Under Development':
      return 'Not captured yet'
    default:
      return label
  }
}

export function formatPublishFlowMovedToStageCopy(
  count: number,
  nextStageLabel: string,
  options: { unchanged?: boolean } = {},
): string {
  const safe = Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0
  const noun = safe === 1 ? 'vehicle' : 'vehicles'
  if (options.unchanged) {
    return `${safe} ${noun} moved to ${nextStageLabel} unchanged.`
  }
  return `${safe} ${noun} moved to ${nextStageLabel}.`
}

export function publishFlowPreProcessorCardSummaryCopy(filteredOut: number, passed: number): string {
  const filteredVerb = filteredOut === 1 ? 'vehicle was' : 'vehicles were'
  const readyVerb = passed === 1 ? 'vehicle is' : 'vehicles are'
  return `${filteredOut} ${filteredVerb} filtered out. ${passed} ${readyVerb} ready for feed checks.`
}

export function publishFlowPreProcessorCardInputCopy(sourceName: string, vinCount: number): string {
  const safe = Number.isFinite(vinCount) && vinCount >= 0 ? Math.floor(vinCount) : 0
  const vins = safe === 1 ? 'VIN' : 'VINs'
  return `${sourceName} — ${safe} ${vins}`
}

export function publishFlowPreProcessorCardResultCopy(filteredOut: number, passed: number): string {
  return `${filteredOut} filtered out, ${passed} passed`
}

export const friendlyStageLabel = friendlyPublishFlowStageLabel

/**
 * Per-record outcome label for the publish-run VIN trail. Past-tense by
 * design: this is reporting what *did* happen during a completed run.
 *
 * Mirrors `friendlyDecisionStatus` from the Preview helpers but never
 * emits "Will change" / "No change" / "Skipped" — those imply tentative
 * or in-progress evaluation.
 */
export function publishFlowDecisionStatusCopy(
  status: PublishDecisionStatus | string,
): string {
  switch (status) {
    case 'changed':
      return 'Changed'
    case 'unchanged':
      return 'Was not affected'
    case 'skipped':
      return 'Was skipped'
    case 'error':
      return 'Could not be evaluated'
    default:
      // Defensive fallback for unknown statuses; never expose the raw
      // identifier to the dealer.
      return 'Outcome unknown'
  }
}

export type PublishFlowAvailabilityCopyOpts = {
  /** Only meaningful when availability === 'available'. */
  hasRecord?: boolean
  /** Reason supplied by the manifest (`unsupportedReason` / `deferredReason`). */
  reason?: string
}

/**
 * Per-stage availability label for the publish-run VIN trail. Past-tense
 * counterpart of `friendlyAvailabilityCopy`.
 *
 * The two empty-record branches are intentionally distinct:
 *   - `available` + no record for this VIN  → "Was not affected"
 *     (the area was evaluated; this VIN was untouched).
 *   - `unsupported` / `deferred` / `notConfigured` → "Not captured for this run"
 *     (the area was not evaluated; absence is by design).
 *
 * Surfacing both as the same message would be misleading.
 */
export function publishFlowAvailabilityCopy(
  availability: PublishStageAvailability,
  opts: PublishFlowAvailabilityCopyOpts = {},
): { label: string; detail?: string } {
  switch (availability) {
    case 'available':
      if (opts.hasRecord) {
        return { label: 'Was updated' }
      }
      return { label: 'Was not affected' }
    case 'unsupported':
      return {
        label: 'Not captured for this run',
        detail: opts.reason,
      }
    case 'deferred':
      return {
        label: 'Not captured for this run',
        detail: opts.reason,
      }
    case 'notConfigured':
      return { label: 'Not configured for this run' }
    default:
      // Defensive fallback for future availability values; never expose
      // the raw token.
      return { label: 'Not captured for this run' }
  }
}

// ---------------------------------------------------------------------------
// Phase 7.y — Groups decision-record copy.
// ---------------------------------------------------------------------------

/**
 * Phase 7.y — Guardrail #4. Friendly banner copy shown when a groups
 * decision record carries `provenance.priorReadFailed === true`. The
 * raw flag is NEVER surfaced in the UI; consumers translate it to
 * this string.
 *
 * Co-located with the other label helpers so the writing style stays
 * consistent (past-tense, dealer-friendly, no internal vocabulary).
 */
export function publishFlowGroupsPriorReadFailedCopy(): string {
  return 'Prior group membership could not be fully determined for this run.'
}

/**
 * Phase 7.y — Guardrail #8. "+N more" toggle copy for collapsed
 * Added/Removed segment lists. Pluralization is intentionally trivial
 * ("+1 more" / "+3 more"); a richer i18n surface can replace this
 * helper without touching the component.
 */
export function publishFlowGroupsSegmentsCollapseCopy(remaining: number): string {
  const safe = Number.isFinite(remaining) && remaining > 0 ? Math.floor(remaining) : 0
  return `+${safe} more`
}

/**
 * Phase 7.y — Guardrail #8. Single source of truth for the collapse
 * threshold applied to groups segment lists (Added AND Removed,
 * identically). Keeping this as a named constant prevents the value
 * from drifting between the component and its tests, and from
 * being applied differently to the two lists.
 */
export const SEGMENT_LIST_COLLAPSE_THRESHOLD = 5

// ---------------------------------------------------------------------------
// Phase 8.y — fieldMapping decision-record copy + display constants.
// ---------------------------------------------------------------------------

/**
 * Phase 8.y G2/G3 (priorReadFailed). Friendly banner copy shown
 * inside the EXPANDED panel of a fieldMapping VIN-trail row when
 * `record.provenance.priorReadFailed === true`. The raw flag is
 * NEVER surfaced in the UI; consumers translate it to this string.
 *
 * Distinct from the groups equivalent because the failure mode is
 * different: groups loses prior membership (so removals are
 * unknowable); fieldMapping loses prior per-field values (so the
 * `Before` cell of an unknown field is omitted, but the `After`
 * value remains trustworthy).
 */
export function publishFlowFieldMappingPriorReadFailedCopy(): string {
  return 'Prior vehicle data could not be fully determined for this run.'
}

/**
 * Phase 8.y G2 — collapsed-summary primary line. Pluralization is
 * intentionally trivial ("1 field updated" / "N fields updated");
 * a richer i18n surface can replace this helper without touching
 * the component.
 *
 * Defensive on non-finite / negative counts: clamps to 0 ("0 fields
 * updated") so a malformed record never crashes the row.
 */
export function publishFlowFieldMappingFieldsUpdatedCopy(count: number): string {
  const safe = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  return safe === 1 ? '1 field updated' : `${safe} fields updated`
}

/**
 * Phase 8.y G4 — within-expanded "+N more" toggle copy. Mirrors
 * `publishFlowGroupsSegmentsCollapseCopy` so both stages produce
 * the same overflow grammar. Defensive on non-finite / negative
 * remainders.
 */
export function publishFlowFieldMappingFieldsCollapseCopy(remaining: number): string {
  const safe = Number.isFinite(remaining) && remaining > 0 ? Math.floor(remaining) : 0
  return `+${safe} more`
}

/**
 * Phase 8.y G4 — single source of truth for the collapse threshold
 * applied INSIDE the expanded fieldMapping panel. Records with
 * field-change counts greater than this render only the first
 * threshold rows; the overflow is hard-gated by `v-if` (NOT
 * `v-show`) so unrendered rows are never in the DOM.
 */
export const FIELD_CHANGES_COLLAPSE_THRESHOLD = 5

/**
 * Phase 8.y G3 — em-dash sentinel rendered for missing
 * before/after values. A `Before` cell shows this when the prior
 * value was omitted (per-field `priorReadFailed`). An `After`
 * cell shows it only if the writer unexpectedly produced a record
 * without `afterValue` (a defensive fallback; the component logs
 * a one-shot warn in that case).
 */
export const EMPTY_VALUE_SENTINEL = '\u2014'

/**
 * Phase 8.y G3 — client-side truncation thresholds. Values longer
 * than these caps render with ellipsis CSS and a `title` tooltip
 * exposing the full string. Shared between the component and its
 * tests to avoid drift.
 */
export const VALUE_TRUNCATE_LIMIT = 80
export const FIELD_NAME_TRUNCATE_LIMIT = 40

// ---------------------------------------------------------------------------
// listingRules decision-record copy.
// ---------------------------------------------------------------------------

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function readListedVisibilityFlag(obj: Record<string, unknown>): boolean | undefined {
  if (typeof obj.accepted === 'boolean') return obj.accepted
  if (obj.membership === 'accepted') return true
  if (obj.membership === 'blocked') return false
  if (typeof obj.listed === 'boolean') return obj.listed
  if (obj.decision === 'listed') return true
  if (obj.decision === 'delisted') return false
  return undefined
}

/**
 * Past-tense inventory membership (`listingRules`) detail for a single
 * VIN decision record in the publish-run trail.
 */
export function publishFlowListingRulesVinDetailCopy(record: PublishDecisionRecord): string {
  if (record.status === 'error') {
    return 'Inventory membership could not be evaluated for this vehicle.'
  }
  if (record.status === 'skipped') {
    return 'Inventory membership was skipped for this vehicle.'
  }

  const before = isRecordObject(record.before) ? record.before : {}
  const after = isRecordObject(record.after) ? record.after : {}
  const beforeListed = readListedVisibilityFlag(before)
  const afterListed = readListedVisibilityFlag(after)

  if (beforeListed === false && afterListed === true) {
    return 'Accepted'
  }
  if (beforeListed === true && afterListed === false) {
    return 'Blocked'
  }
  if (afterListed === true) {
    return 'Accepted into inventory'
  }
  if (afterListed === false) {
    return 'Blocked from inventory'
  }

  return 'Inventory membership was evaluated for this vehicle.'
}

// ---------------------------------------------------------------------------
// Phase 10.3.y — feedFieldRules decision-record copy.
// ---------------------------------------------------------------------------

/**
 * Phase 10.3.y — Collapsed-summary primary line for the
 * `FeedFieldRulesDecisionTrailRow` component when the writer
 * resolved a vehicle condition for the VIN.
 *
 * Surfaces the resolved condition string inline so the dealer sees
 * *what* was selected without having to expand the row, while keeping
 * internal vocabulary (`feedFieldRules`, `selectedCondition`,
 * `selectedRuleIndex`, etc.) out of the rendered text.
 *
 * Defensive on missing / blank values: returns the no-condition
 * variant so a malformed record never crashes the row or surfaces a
 * raw token like `undefined` to the dealer.
 */
export function publishFlowFeedFieldRulesSelectedCopy(condition?: string): string {
  const trimmed = typeof condition === 'string' ? condition.trim() : ''
  if (trimmed.length === 0) {
    return 'Feed field rules resolved for this run.'
  }
  return `Feed field rules: ${trimmed}.`
}

/**
 * Phase 10.3.y — Collapsed-summary primary line for records the
 * writer marked `status === 'skipped'` because no feed field rules
 * were configured for the account's feed.
 *
 * The text never references the `noRulesConfigured` flag itself; it
 * tells the dealer the user-facing outcome.
 */
export function publishFlowFeedFieldRulesNoRulesCopy(): string {
  return 'No feed field rules were configured for this run.'
}

/**
 * Phase 10.3.y — Collapsed-summary primary line for records the
 * writer marked `status === 'error'`. Mirrors the
 * `publishFlowDecisionStatusCopy('error')` shape ("Could not be
 * evaluated") but adds stage-specific framing so the dealer
 * understands which surface failed without leaking internal tokens.
 */
export function publishFlowFeedFieldRulesErrorCopy(): string {
  return 'Feed field rules could not be determined for this run.'
}

/**
 * Sentinel rendered in the Feed Field Rules drill-down when a before/after
 * value is absent (null, undefined, or empty). Past-tense, dealer-facing,
 * never a raw token.
 */
export const FEED_FIELD_RULE_NO_VALUE = 'No value'

/**
 * Format a single feed-field-rule before/after value for the VIN drill-down.
 *
 * The writer bounds these to scalars (string / finite number / boolean /
 * null) via `safelyBoundedValue`, so this never has to dump raw JSON.
 * Booleans render as Yes/No for dealer readability; empty / missing values
 * collapse to the `No value` sentinel so a diff always reads cleanly
 * (e.g. `No value → 2500`).
 */
export function publishFlowFeedFieldRuleValueCopy(value: unknown): string {
  if (value === null || value === undefined) return FEED_FIELD_RULE_NO_VALUE
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length === 0 ? FEED_FIELD_RULE_NO_VALUE : trimmed
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : FEED_FIELD_RULE_NO_VALUE
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  // Arrays / objects are never expected here (writer bounds to scalars);
  // never surface a raw structure to the dealer.
  return FEED_FIELD_RULE_NO_VALUE
}

/**
 * Resolve the dealer-facing field label for a feed-field-rule reason.
 * Prefers the configured human label (`configLabel`, the writer's
 * `targetFieldLabel`), falling back to the raw target field name. Returns
 * an empty string when neither is usable so the caller can drop the entry.
 */
export function publishFlowFeedFieldRuleFieldLabel(reason: {
  configLabel?: unknown
  field?: unknown
}): string {
  const label = typeof reason.configLabel === 'string' ? reason.configLabel.trim() : ''
  if (label.length > 0) return label
  const field = typeof reason.field === 'string' ? reason.field.trim() : ''
  return field
}

/**
 * Resolve the optional "Why" line for a feed-field-rule changed field.
 *
 * Surfaces `reasons[].explanation` ONLY when it adds information beyond the
 * field label and feed source the row already renders. The writer's default
 * sentence ("<label> was changed by a feed field rule from <feed>.") merely
 * restates those two facts, so it is suppressed; a genuinely informative
 * explanation (e.g. one that names the matched condition) passes through.
 * Returns `null` when there is nothing useful to show.
 */
export function publishFlowFeedFieldRuleWhyCopy(input: {
  explanation?: unknown
  label?: string
  source?: string
}): string | null {
  const explanation = typeof input.explanation === 'string' ? input.explanation.trim() : ''
  if (explanation.length === 0) return null
  const label = typeof input.label === 'string' ? input.label.trim() : ''
  const source = typeof input.source === 'string' ? input.source.trim() : ''
  if (label.length > 0 && source.length > 0) {
    const redundantDefault = `${label} was changed by a feed field rule from ${source}.`
    if (normalizeForCompare(explanation) === normalizeForCompare(redundantDefault)) {
      return null
    }
  }
  return explanation
}

/**
 * Resolve an optional dealer-facing "Rule" / action label for a
 * feed-field-rule reason.
 *
 * Feed field rule configs do NOT carry a stable rule id/name today, so this
 * never invents one. It surfaces the writer's `transformName` (the configured
 * action type, e.g. `replace`) as a humanized verb when present, and returns
 * `null` otherwise so the caller omits the line entirely. The raw target field
 * label (`configLabel`) is intentionally NOT used here — it already renders as
 * the field heading, so repeating it as a "Rule" line would be noise. A raw
 * positional index is likewise never surfaced.
 */
export function publishFlowFeedFieldRuleActionLabel(reason: {
  transformName?: unknown
}): string | null {
  const raw = typeof reason.transformName === 'string' ? reason.transformName.trim() : ''
  if (raw.length === 0) return null
  return humanizeFeedFieldRuleAction(raw)
}

/** Known action types mapped to readable, non-fabricated verbs. */
const FEED_FIELD_RULE_ACTION_LABELS: Record<string, string> = {
  replace: 'Replace value',
  set: 'Set value',
  append: 'Append value',
  prepend: 'Prepend value',
  remove: 'Remove value',
  clear: 'Clear value',
  overwrite: 'Overwrite value',
}

/**
 * Humanize a raw action token without inventing meaning: known actions map to
 * a readable verb; anything else is split on camelCase / separators and
 * sentence-cased so the dealer never sees a raw identifier like `set_value`.
 */
function humanizeFeedFieldRuleAction(action: string): string {
  const normalized = action.toLowerCase()
  if (FEED_FIELD_RULE_ACTION_LABELS[normalized]) {
    return FEED_FIELD_RULE_ACTION_LABELS[normalized]
  }
  const words = action
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (words.length === 0) return action
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Neutral past-tense line for a `changed` feed-field-rule record whose
 * `reasons[]` carried no usable per-field detail (defensive — the writer
 * always emits field detail for changed records today). Intentionally does
 * NOT reuse the legacy condition-rule "resolved for this run" phrasing.
 */
export function publishFlowFeedFieldRulesGenericChangedCopy(): string {
  return 'Feed field rules updated this vehicle.'
}

/** Collapse whitespace + lowercase for tolerant copy comparison. */
function normalizeForCompare(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase()
}

/** Preview-aligned hint shown under the Feed Field Rules subsection title. */
export const PUBLISH_FLOW_FEED_FIELD_RULES_SUBSECTION_HINT =
  'Used by Vehicle data to transform feed values after mapping.'

/**
 * Summary line for the Feed Field Rules subsection nested inside the
 * Field Mapping stage card on Publish Flow.
 */
export function publishFlowFeedFieldRulesSubsectionSummaryCopy(input: {
  status:
    | 'available'
    | 'empty'
    | 'notConfigured'
    | 'skipped'
    | 'unsupported'
    | 'error'
  changedCount: number
}): string {
  if (input.status === 'notConfigured') {
    return 'No feed field rules were configured for this publish run.'
  }
  if (input.status === 'error') {
    return 'Feed field rules could not be fully evaluated for this publish run.'
  }
  const changed = Number.isFinite(input.changedCount) ? Math.max(0, Math.floor(input.changedCount)) : 0
  if (input.status === 'empty' || changed === 0) {
    return 'Feed field rules were evaluated; no vehicle fields changed.'
  }
  const noun = changed === 1 ? 'vehicle had' : 'vehicles had'
  return `${changed} ${noun} feed field rule updates during mapping.`
}

/**
 * Result line for the Vehicle Groups stage card on Publish Flow.
 * Uses manifest `counts.changed` when available; falls back to generic
 * copy when the aggregate is missing.
 */
export function publishFlowGroupsChangedResultCopy(
  changedCount: number | null | undefined,
): string {
  if (typeof changedCount !== 'number' || !Number.isFinite(changedCount)) {
    return 'Vehicle group membership was evaluated. View groups to inspect changed memberships.'
  }
  const changed = Math.max(0, Math.floor(changedCount))
  if (changed === 0) return 'No vehicles changed groups'
  const noun = changed === 1 ? 'vehicle' : 'vehicles'
  return `${changed} ${noun} changed groups`
}

// ---------------------------------------------------------------------------
// Phase 10.4.y — preProcessor decision-record copy.
// ---------------------------------------------------------------------------

export type PublishFlowPreProcessorActionCopyInput = {
  mode?: unknown
  inputScope?: unknown
  dedupeWonOverSource?: unknown
}

/**
 * Phase 10.4.y — Collapsed-summary primary line for source-written
 * `preProcessor` records. Copy is action-specific when the bounded
 * provenance hints are available, but never renders raw transform
 * names, JUIDs, S3 locations, or provenance keys.
 */
export function publishFlowPreProcessorActionCopy(
  input: PublishFlowPreProcessorActionCopyInput = {},
): string {
  if (input.mode === 'sharedInventory') {
    return 'Vehicle was included from shared inventory.'
  }
  if (input.mode === 'groupSite') {
    if (input.inputScope === 'groupSiteTarget' && input.dedupeWonOverSource === true) {
      return 'Vehicle from this dealer’s feed was kept over a duplicate.'
    }
    if (input.inputScope === 'groupSiteTarget') {
      return 'Vehicle from this dealer’s feed was included in the group-site output.'
    }
    if (input.inputScope === 'groupSiteSource') {
      return 'Vehicle from a source dealer was included in this dealer’s output.'
    }
    return 'Vehicle was included in the group-site output.'
  }
  return 'Vehicle was included before publish rules ran.'
}

export function publishFlowPreProcessorErrorCopy(): string {
  return 'Inventory preparation could not be evaluated for this run.'
}
